import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';

import Modal from 'components/Modal';
import { Form } from 'antd';
import PackageForm from './PackageForm';
import toast from 'utils/toast';
import styled from 'styled-components';
import settingApis from 'features/settingService/services/apis';
import { getMessageError, IResponseDataBody } from 'services/response';
import { IPackageProgram } from 'features/settingService/services/types/packageSetting';
import { PACKAGE_TYPE } from 'features/settingService/services/constants';
import useGetPackageProgramList from 'features/settingService/services/hooks/useGetPackageProgramList';

export interface CreatePackageModalRef {
  open: (activePackage?: IPackageProgram | null) => void;
  close: () => void;
}

export interface CreatePackageModalProps {}

export interface PackageFormValues {
  name: string;
  price: number;
  validityMonths: number;
  description: string;
  tiers: Array<{
    id?: string;
    name: string;
    quantity: number;
    discountType: string;
    discountValue: number;
    sortOrder: number;
    type: string;
  }>;
}

const CreatePackageModal = forwardRef<
  CreatePackageModalRef,
  CreatePackageModalProps
>((_, ref) => {
  const [visible, setVisible] = useState(false);

  const [activePackage, setActivePackage] = useState<IPackageProgram | null>(
    null,
  );
  const [, mutate] = useGetPackageProgramList();

  const [form] = Form.useForm<PackageFormValues>();

  useImperativeHandle(ref, () => ({
    open: (packageData) => {
      setActivePackage(packageData || null);
      setVisible(true);
    },

    close: () => {
      setVisible(false);
      setActivePackage(null);
      form.resetFields();
    },
  }));

  const handleClose = () => {
    setVisible(false);
    setActivePackage(null);
    form.resetFields();
  };

  const handleSubmit = async (payload: PackageFormValues) => {
    try {
      console.log('Payload gửi BE:', payload);

      let res: IResponseDataBody;

      if (activePackage?.id) {
        res = await settingApis.updatePackageProgram({
          id: activePackage.id,
          body: {
            name: payload.name,
            description: payload.description,
            price: payload.price,
            validityMonths: payload.validityMonths,
            tiers: payload.tiers,
          },
        });
      } else {
        res = await settingApis.createPackageProgram(payload);
      }

      if (res?.data?.data) {
        toast.success(
          activePackage?.id
            ? 'Update package successfully'
            : 'Create package successfully',
        );
        mutate();
        handleClose();
      }
    } catch (error) {
      toast.error(
        getMessageError(error) ||
          (activePackage?.id
            ? 'Update package failed'
            : 'Create package failed'),
      );
    }
  };

  useEffect(() => {
    if (!visible) return;

    if (activePackage) {
      form.setFieldsValue({
        name: activePackage.name,
        price: activePackage.price,
        validityMonths: activePackage.validityMonths,
        description: activePackage.description,
        tiers: activePackage.tiers.map((tier) => ({
          id: tier.id,
          name: tier.name,
          quantity: tier.quantity,
          discountType: tier.discountType,
          discountValue: tier.discountValue,
          sortOrder: tier.sortOrder,
          type: tier.type || PACKAGE_TYPE.BRONZE,
        })),
      });
    } else {
      form.resetFields();
    }
  }, [activePackage, visible, form]);

  return (
    <Modal
      v2
      visible={visible}
      width={1680}
      modalTitle={activePackage ? 'UPDATE PACKAGE' : 'NEW PACKAGE'}
      okTitle='OK'
      cancelTitle='Close'
      onClose={handleClose}
      onSubmit={() => form.submit()}
      noneBodyStyle
      noneFooterStyle
      containerPadding={0}
      destroyOnClose
    >
      <Container>
        <PackageForm
          form={form}
          onSubmit={handleSubmit}
          isEdit={!!activePackage}
        />
      </Container>
    </Modal>
  );
});

CreatePackageModal.displayName = 'CreatePackageModal';
export default CreatePackageModal;

const Container = styled.div`
  padding: 24px;
  padding-bottom: 40px;
  background: #f6f7fc;
`;
